<template>
  <div>
    <h1 class="h3 mb-3 text-gray-800">{{ $t("Histórico de Importações") }}</h1>

    <b-card class="shadow">
      <div class="list-importacao-component">
        <div class="row">
          <div class="col-12">
            <div class="d-flex align-items-center justify-content-between mb-4">
              <div>
                <button
                  class="btn btn-primary"
                  @click="$refs.uploadImportacaoModal.abrirModal()"
                >
                  <i class="fas fa-cloud-upload-alt"></i>
                  {{ $t("Nova Importação") }}
                </button>

                <UploadImportacaoModal
                  ref="uploadImportacaoModal"
                  @uploaded="getImportacao"
                />

                <CancelarImportacaoModal
                  ref="cancelarImportacaoModal"
                  @confirm="cancelImportacao"
                />

                <ReprocessarImportacaoModal
                  ref="reprocessarImportacaoModal"
                  @confirm="reprocessImportacao"
                />
              </div>

              <div>
                <div class="input-group mb-3">
                  <b-input
                    :placeholder="$t('Type to search')"
                    class="form-control search-input"
                    type="text"
                    v-model="search"
                    @keyup.enter="
                      page = 1;
                      getImportacao();
                    "
                    aria-describedby="input-search"
                  />
                  <div
                    class="input-group-append"
                    role="button"
                    @click="
                      page = 1;
                      getImportacao();
                    "
                  >
                    <span class="input-group-text" id="input-search">
                      <i class="fas fa-search search-icon" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12">
            <loading-animation
              :active.sync="loading"
              :is-full-page="false"
              :opacity="1"
              color="#5895FF"
            />

            <b-table
              :fields="[
                { key: 'created_at', label: $t('Criada em') },
                { key: 'scheduled_at', label: $t('Agendada para') },
                { key: 'started_at', label: $t('Início') },
                { key: 'finished_at', label: $t('Fim') },
                { key: 'failed_at', label: $t('Falha em') },
                { key: 'original_filename', label: $t('Arquivo') },
                { key: 'status', label: $t('Status') },
                { key: 'user', label: $t('Responsável') },
                { key: 'duracao', label: $t('Duração') },
                { key: 'actions', label: $t('Ações'), class: 'text-right' },
              ]"
              :items="importacaoList"
              :responsive="true"
              striped
              fixed
              hover
              show-empty
              small
              outlined
              stacked="md"
              table-class="mb-3"
            >
              <template v-slot:cell(created_at)="data">
                {{ data.item.created_at || "-" }}
              </template>

              <template v-slot:cell(scheduled_at)="data">
                {{ data.item.scheduled_at || "-" }}
              </template>

              <template v-slot:cell(started_at)="data">
                {{ data.item.started_at || "-" }}
              </template>

              <template v-slot:cell(finished_at)="data">
                {{ data.item.finished_at || "-" }}
              </template>

              <template v-slot:cell(failed_at)="data">
                <span
                  v-if="data.item.status === 'falha'"
                  class="text-danger font-weight-bold"
                >
                  {{ data.item.failed_at || "-" }}
                </span>
                <span v-else>-</span>
              </template>

              <template v-slot:cell(original_filename)="data">
                <div>
                  <strong>{{ data.item.original_filename || "-" }}</strong>

                  <div class="text-muted small" v-if="data.item.file_extension">
                    .{{ data.item.file_extension }}
                  </div>

                  <div
                    v-if="
                      data.item.failure_message && data.item.status === 'falha'
                    "
                    class="text-danger small mt-1"
                    :title="data.item.failure_message"
                  >
                    {{ limitText(data.item.failure_message, 80) }}
                  </div>
                </div>
              </template>

              <template v-slot:cell(status)="data">
                <span
                  class="badge-importacao"
                  :class="getStatusClass(data.item.status)"
                >
                  {{ getStatusLabel(data.item.status) }}
                </span>
              </template>

              <template v-slot:cell(user)="data">
                <span v-if="data.item.user">
                  {{ data.item.user.name }}
                </span>
                <span v-else>-</span>
              </template>

              <template v-slot:cell(duracao)="data">
                {{ data.item.duracao || "-" }}
              </template>

              <template v-slot:cell(actions)="data">
                <div class="btn-group">
                  <router-link
                    v-if="canViewDetails(data.item)"
                    class="btn btn-primary btn-sm"
                    :title="$t('Ver detalhes')"
                    :to="`/dashboard/importacao/${data.item.id}`"
                  >
                    <i class="mdi mdi-text-box-search-outline"></i>
                  </router-link>

                  <button
                    v-if="canCancel(data.item)"
                    class="btn btn-warning btn-sm"
                    :title="$t('Cancelar importação agendada')"
                    :disabled="actionLoadingId === data.item.id"
                    @click="abrirModalCancelamento(data.item)"
                  >
                    <i class="fas fa-ban"></i>
                  </button>

                  <button
                    v-if="canReprocess(data.item)"
                    class="btn btn-success btn-sm"
                    :title="$t('Processar novamente')"
                    :disabled="actionLoadingId === data.item.id"
                    @click="abrirModalReprocessamento(data.item)"
                  >
                    <i class="fas fa-redo"></i>
                  </button>
                </div>
              </template>
            </b-table>

            <div class="row align-items-center">
              <div class="col-lg-6 col-md-12">
                <div class="form-group d-inline-flex mb-0">
                  <select
                    class="form-control"
                    style="width: 70px"
                    v-model="perPage"
                    v-on:change="getImportacao"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                </div>

                <div class="d-inline-flex ml-2">
                  <b-pagination
                    :per-page="perPage"
                    :total-rows="pagination.total"
                    @change="changePage"
                    v-model="page"
                  />
                </div>
              </div>

              <div class="col-lg-6 col-md-12 mt-4 mt-lg-0">
                <div class="float-lg-right">
                  {{ $t("Showing page") }} {{ pagination.currentPage }}
                  {{ $t("of") }}
                  {{ pagination.totalPages }}
                  {{ $t("pages in") }}
                  {{ pagination.total }}
                  {{ $t("records") }}
                </div>
              </div>
            </div>
          </div>

          <router-view />
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import UploadImportacaoModal from "./UploadImportacaoModal.vue";
import CancelarImportacaoModal from "./CancelarImportacaoModal.vue";
import ReprocessarImportacaoModal from "./ReprocessarImportacaoModal.vue";

export default {
  name: "Importacao",
  metaInfo() {
    return {
      title: this.$i18n.t("Importação"),
    };
  },
  components: {
    UploadImportacaoModal,
    CancelarImportacaoModal,
    ReprocessarImportacaoModal,
  },
  data() {
    return {
      page: 1,
      search: "",
      perPage: 10,
      loading: true,
      actionLoadingId: null,
      pagination: {
        currentPage: 0,
        perPage: 0,
        total: 0,
        totalPages: 0,
      },
      itemSelected: [],
      importacaoList: [],
    };
  },
  mounted() {
    this.getImportacao();
  },
  methods: {
    changePage(page) {
      if (
        page > 0 &&
        page <= this.pagination.totalPages &&
        page !== this.page
      ) {
        this.page = page;
        this.getImportacao();
      }
    },

    getImportacao() {
      this.loading = true;

      axios
        .get("api/dashboard/importacao", {
          params: {
            page: this.page,
            search: this.search,
            perPage: this.perPage,
          },
        })
        .then((response) => {
          this.importacaoList = response.data.data || [];
          this.pagination = response.data.pagination || {
            currentPage: 1,
            perPage: this.perPage,
            total: 0,
            totalPages: 1,
          };

          if (
            this.pagination.totalPages > 0 &&
            this.pagination.totalPages < this.pagination.currentPage
          ) {
            this.page = this.pagination.totalPages;
            this.getImportacao();
          } else {
            this.loading = false;
            this.actionLoadingId = null;
          }
        })
        .catch(() => {
          this.importacaoList = [];
          this.loading = false;
          this.actionLoadingId = null;
        });
    },

    canCancel(item) {
      return item && item.status === "pendente";
    },

    canReprocess(item) {
      return item && item.status === "falha";
    },

    limitText(text, limit = 80) {
      if (!text) {
        return "-";
      }

      if (text.length <= limit) {
        return text;
      }

      return `${text.substring(0, limit)}...`;
    },

    abrirModalCancelamento(item) {
      if (!item || !item.id) {
        return;
      }

      this.$refs.cancelarImportacaoModal.abrirModal(item);
    },

    abrirModalReprocessamento(item) {
      if (!item || !item.id) {
        return;
      }

      this.$refs.reprocessarImportacaoModal.abrirModal(item);
    },

    cancelImportacao(payload) {
      const importacao = payload.importacao;

      if (!importacao || !importacao.id) {
        return;
      }

      this.actionLoadingId = importacao.id;

      axios
        .post(`api/dashboard/importacao/${importacao.id}/cancelar`, {
          delete_file: payload.delete_file,
        })
        .then(() => {
          this.getImportacao();
        })
        .catch(() => {
          this.actionLoadingId = null;
        });
    },

    reprocessImportacao(item) {
      if (!item || !item.id) {
        return;
      }

      this.actionLoadingId = item.id;

      axios
        .post(`api/dashboard/importacao/${item.id}/reprocessar`)
        .then(() => {
          this.getImportacao();
        })
        .catch(() => {
          this.actionLoadingId = null;
        });
    },

    getStatusLabel(status) {
      const statuses = {
        pendente: this.$t("Pendente"),
        cancelada: this.$t("Cancelada"),
        processando: this.$t("Processando"),
        falha: this.$t("Falha"),
        finalizada: this.$t("Finalizada"),
      };

      return statuses[status] || status || "-";
    },

    getStatusClass(status) {
      const classes = {
        pendente: "badge-importacao-pendente",
        cancelada: "badge-importacao-cancelada",
        processando: "badge-importacao-processando",
        falha: "badge-importacao-falha",
        finalizada: "badge-importacao-finalizada",
      };

      return classes[status] || "badge-importacao-default";
    },

    canViewDetails(item) {
      return (
        item && ["processando", "falha", "finalizada"].includes(item.status)
      );
    },
  },
};
</script>

<style scoped>
.badge-importacao {
  display: inline-block;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.25rem;
}

.badge-importacao-pendente {
  color: #856404;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
}

.badge-importacao-cancelada {
  color: #383d41;
  background-color: #e2e3e5;
  border: 1px solid #d6d8db;
}

.badge-importacao-processando {
  color: #004085;
  background-color: #cce5ff;
  border: 1px solid #b8daff;
}

.badge-importacao-falha {
  color: #721c24;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
}

.badge-importacao-finalizada {
  color: #155724;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
}

.badge-importacao-default {
  color: #383d41;
  background-color: #e2e3e5;
  border: 1px solid #d6d8db;
}
</style>
